import { apiClient } from './api';

// NOTE: the backend sends `contextLimit` / `usage.limit` as Infinity for the
// enterprise tier, which JSON-serialises to `null`. Treat a null limit as
// "unlimited" in the UI.
export interface BillingPlan {
  type: string;
  displayName: string;
  contextLimit: number | null;
  // Present on the current plan; true when it's a legacy tier no longer offered.
  retired?: boolean;
}

// Live price from Stripe (never hardcoded). unitAmount is in the currency's
// minor unit (cents); null when billing is unconfigured or the tier has no price.
export interface PlanPrice {
  unitAmount: number | null;
  currency: string;
  interval: string | null;
}

export interface AvailablePlan extends BillingPlan {
  purchasable: boolean;
  price: PlanPrice | null;
}

export interface BillingUsage {
  used: number;
  limit: number | null;
  plan: string;
}

export interface BillingSubscription {
  status: string | null;
  currentPeriodEnd: string | null;
  hasBillingAccount: boolean;
}

export type PlanSource = 'none' | 'stripe' | 'manual';

export interface BillingStatus {
  billingConfigured: boolean;
  // Who owns the plan: 'manual' = invoice/PO managed by the account team
  // (self-serve checkout/portal are not offered).
  planSource: PlanSource;
  plan: BillingPlan;
  subscription: BillingSubscription;
  usage: BillingUsage;
  availablePlans: AvailablePlan[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * REV-318: client for the org billing endpoints. getStatus reads local plan +
 * usage state; createCheckout / createPortal return a hosted Stripe URL the
 * caller should redirect the browser to.
 */
export const billingService = {
  getStatus: async (orgId: string): Promise<BillingStatus> => {
    const res = await apiClient.get<ApiResponse<BillingStatus>>(
      `/v1/organizations/${orgId}/billing`
    );
    return res.data;
  },

  createCheckout: async (orgId: string, planType: string): Promise<string> => {
    const res = await apiClient.post<ApiResponse<{ url: string }>>(
      `/v1/organizations/${orgId}/billing/checkout`,
      { planType }
    );
    return res.data.url;
  },

  createPortal: async (orgId: string): Promise<string> => {
    const res = await apiClient.post<ApiResponse<{ url: string }>>(
      `/v1/organizations/${orgId}/billing/portal`,
      {}
    );
    return res.data.url;
  },
};
